/**
 * Edit page for a single conference room. Replaces the inline edit
 * DIALOG on the Conference Rooms list with a full-page editor that
 * carries two tabs:
 *
 *   - "General"  → the same fields the dialog had (name, PINs, mode,
 *                  max participants, active flag) wired to the existing
 *                  `conferenceRooms.update` PATCH.
 *   - "Advanced" → the sectioned `AdvancedOptionsTab` shipped in Phase
 *                  12.2 (per-room behaviour toggles, recording mode,
 *                  promotion key, limits, dial-out, Q&A clip).
 *
 * The list page's dialog is still wired (kept as a quick-edit fallback),
 * but the row action also opens this page so power users get the full
 * surface. The route is registered as a customer-scoped child so the
 * existing customer-context machinery (`ScopedCustomerLayout`) seeds
 * tenant scope from the URL.
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';

import { conferenceRooms } from '@/api/conferenceRooms';
import type { ConferenceMode, ConferenceRoomRow } from '@/api/conferenceRooms';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { formatRef } from '@/lib/formatRef';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { AdvancedOptionsTab } from './AdvancedOptionsTab';

const MUTATE_ROLES = ['super_admin', 'reseller_owner', 'office_manager'];

export function EditConferenceRoomPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();

  const { data: room, isLoading, error } = useQuery({
    queryKey: ['conferenceRoom', roomId],
    queryFn: () => conferenceRooms.get(roomId!),
    enabled: Boolean(roomId),
  });

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading conference room…</div>;
  }
  if (error || !room) {
    return (
      <div className="text-sm text-destructive">
        Conference room not found.{' '}
        <button type="button" className="underline" onClick={() => navigate(-1)}>
          Go back
        </button>
      </div>
    );
  }

  return <EditConferenceRoomPageInner room={room} />;
}

function EditConferenceRoomPageInner({ room }: { room: ConferenceRoomRow }) {
  const user = useCurrentUser();
  const canMutate = MUTATE_ROLES.includes(user.role);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Conference room"
        title={room.name}
        description={`${formatRef(room.ref_number, 'CONF')} — capacity ${room.max_participants}, ${room.mode} mode`}
        actions={(
          <Button asChild size="sm" variant="outline">
            <Link to="/conference-rooms">
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Back to rooms
            </Link>
          </Button>
        )}
      />

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="advanced">Advanced options</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <GeneralSection room={room} canMutate={canMutate} />
        </TabsContent>
        <TabsContent value="advanced">
          <AdvancedOptionsTab roomId={room.id} customerId={room.customer_id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── General tab — name / PINs / mode / max / active ──────────────────────────
//
// Adapted from the body of `EditRoomDialog` in
// `frontend/src/app/portal/conference-rooms/ConferenceRoomsList.tsx` so a
// future change to the dialog's fields should be mirrored here. Submit is
// an explicit "Save changes" — unlike Advanced where each toggle PATCHes
// on change, this section's PIN inputs would be too noisy to auto-save.

function GeneralSection({
  room, canMutate,
}: {
  room: ConferenceRoomRow;
  canMutate: boolean;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(room.name);
  const [newAdminPin, setNewAdminPin] = useState('');
  const [newParticipantPin, setNewParticipantPin] = useState('');
  const [clearParticipantPin, setClearParticipantPin] = useState(false);
  const [mode, setMode] = useState<ConferenceMode>(room.mode);
  const [maxParticipants, setMaxParticipants] = useState(room.max_participants);
  const [active, setActive] = useState(room.active);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => conferenceRooms.update(room.id, {
      name: name.trim() || undefined,
      admin_pin: newAdminPin || undefined,
      participant_pin: clearParticipantPin ? null : (newParticipantPin || undefined),
      mode,
      max_participants: maxParticipants,
      active,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conferenceRooms'] });
      qc.invalidateQueries({ queryKey: ['conferenceRoom', room.id] });
      setNewAdminPin('');
      setNewParticipantPin('');
      setClearParticipantPin(false);
      setError(null);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Save failed.'),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!canMutate || save.isPending) return;
    save.mutate();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">General settings</CardTitle>
        <CardDescription>
          The basics — display name, dial-in PINs, mode, capacity, and
          whether the room accepts new joins.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="er-name">Name</Label>
            <Input
              id="er-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!canMutate}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="er-admin-pin">New admin PIN</Label>
              <Input
                id="er-admin-pin"
                type="password"
                autoComplete="off"
                value={newAdminPin}
                onChange={(e) => setNewAdminPin(e.target.value.replace(/\D/g, '').slice(0, 12))}
                placeholder="Leave blank to keep existing"
                disabled={!canMutate}
              />
              <p className="text-xs text-muted-foreground">
                Admin PIN is {room.has_admin_pin ? 'set' : <Badge variant="secondary">missing</Badge>}.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="er-part-pin">New participant PIN</Label>
              <Input
                id="er-part-pin"
                type="password"
                autoComplete="off"
                disabled={!canMutate || clearParticipantPin}
                value={newParticipantPin}
                onChange={(e) => setNewParticipantPin(e.target.value.replace(/\D/g, '').slice(0, 12))}
                placeholder={room.has_participant_pin ? 'Keep existing (blank)' : 'Set a guest PIN'}
              />
              {room.has_participant_pin && canMutate && (
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer pt-1">
                  <input
                    type="checkbox"
                    checked={clearParticipantPin}
                    onChange={(e) => setClearParticipantPin(e.target.checked)}
                    className="accent-primary"
                  />
                  Clear participant PIN (lock to admins only)
                </label>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Mode</Label>
              <Select
                value={mode}
                onValueChange={(v) => setMode(v as ConferenceMode)}
                disabled={!canMutate}
              >
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="moderated">Moderated</SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="er-max">Max participants</Label>
              <Input
                id="er-max"
                type="number"
                min={2}
                max={500}
                value={maxParticipants}
                onChange={(e) => setMaxParticipants(parseInt(e.target.value, 10) || 50)}
                disabled={!canMutate}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              disabled={!canMutate}
              className="accent-primary"
            />
            Active (inactive rooms reject all join attempts)
          </label>

          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}

          {canMutate && (
            <div className="flex justify-end">
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
